import { useMemo } from 'react';
import { addSeconds, isAfter, parseISO } from 'date-fns';
import { useGetPresignedUrlForS3Url } from '@juno/client-api';
import { useSettings } from '../routing/RoutingMediator';

/**
 * Hook to resolve a resource URL that might be an S3 URL requiring a presigned URL
 * @param resourceUrl The resource URL to resolve (may be S3 or direct)
 * @returns The presigned URL and loading state
 */
export const useGetPresignedUrl = (
  resourceUrl?: string,
): {
  presignedUrl: string;
  isLoadingPresignedUrl: boolean;
} => {
  const { siteId } = useSettings();

  // Check if URL is already signed and not expired
  const urlStatus = useMemo(() => {
    if (!resourceUrl) return { isSigned: false, isExpired: false };

    const isSigned = resourceUrl.includes('X-Amz-Signature=');

    // Check for expiration if signed
    let isExpired = false;
    if (isSigned) {
      try {
        // Extract expiration parameters
        const dateMatch = resourceUrl.match(/X-Amz-Date=(\d{8}T\d{6}Z)/);
        const expiresMatch = resourceUrl.match(/X-Amz-Expires=(\d+)/);

        if (dateMatch && expiresMatch) {
          // Format AWS date string (YYYYMMDDTHHMMSSZ) to ISO format
          const awsDateStr = dateMatch[1];
          const isoDateStr = `${awsDateStr.slice(0, 4)}-${awsDateStr.slice(
            4,
            6,
          )}-${awsDateStr.slice(6, 8)}T${awsDateStr.slice(9, 11)}:${awsDateStr.slice(
            11,
            13,
          )}:${awsDateStr.slice(13, 15)}Z`;

          // Parse the ISO date and calculate expiration
          const signedDate = parseISO(isoDateStr);
          const expiresSeconds = parseInt(expiresMatch[1]);
          const expirationDate = addSeconds(signedDate, expiresSeconds);

          // Compare current time with expiration
          isExpired = isAfter(new Date(), expirationDate);
        }
      } catch (error) {
        console.error('Error checking URL expiration:', error);
      }
    }

    return { isSigned, isExpired };
  }, [resourceUrl]);

  // Get a new presigned URL if needed
  const { data: presignedUrlData, isFetching } = useGetPresignedUrlForS3Url(
    siteId,
    { url: resourceUrl || undefined },
    {
      query: {
        // Get a new URL if it's an S3 URL and either not signed or expired
        enabled:
          !!resourceUrl &&
          resourceUrl.includes('s3.amazonaws.com') &&
          (!urlStatus.isSigned || urlStatus.isExpired),
      },
    },
  );

  return useMemo(() => {
    let presignedUrl = '';

    // If it's already a signed S3 URL and not expired, use it directly
    if (urlStatus.isSigned && !urlStatus.isExpired && resourceUrl) {
      presignedUrl = resourceUrl;
    }
    // If we got a presigned URL from the API, use that
    else if (presignedUrlData?.url) {
      presignedUrl = presignedUrlData.url;
    }
    // If it's a non-S3 URL, use it directly
    else if (resourceUrl && !resourceUrl.includes('s3.amazonaws.com')) {
      presignedUrl = resourceUrl;
    }
    // Fallback to the original URL if all else fails
    else if (resourceUrl) {
      presignedUrl = resourceUrl;
    }

    return { presignedUrl, isLoadingPresignedUrl: isFetching };
  }, [presignedUrlData?.url, resourceUrl, isFetching, siteId, urlStatus]);
};
